// ═══ 업무별 기록 — 목록 한 줄 (CancelAgencyRecords RecordList 에서 분리, 2026-10-22) ═══
// 행 렌더 · 뱃지 · 펼침 상세(연결 업체·연결/해제 버튼·연결 이력) · 탭별 열 정의.
// 상태(선택·펼침·이력·팝업)는 부모(RecordList)가 갖고, 이 파일은 그리기만 한다.
//
// props: row · cols · linkable · open · selected · hist(null=불러오는 중) · busy · onToggle · onSelect · onPick · onUnlink
import React from 'react';
import { useIsMobile } from '../utils/useMobile';
import { formatArrivedAtKst } from '../utils/dateKst';

var EXCLUDE_LABEL = { copy: '청구서 사본', template: '빈 양식 줄', dup_malso: '말소 시트와 중복', no_amount: '금액 없는 행' };
var MODE_LABEL = { manual: '직접 연결', bulk: '일괄 연결', auto: '자동 연결', unlink: '연결 해제' };
// 펼침 상세 라벨 — 이관 산출(records.py) 이 쓰는 detail 키·검토 표시·출처 코드. 모르는 키는 원문 그대로.
var DETAIL_LABEL = { cols: '금액 칸', office_raw: '구청·지명 원문', group_start: '묶음 시작 행', group_total: '엑셀 묶음 합계', date_inherited: '날짜',
  extra_tags: '추가 열 원문', hint_clients: '참고 거래처 후보', ref_rows: '참조 행', copy_kind: '사본 종류', dup_rule: '중복 판정', amount_from: '금액 출처',
  host_row: '같은 행 차량 위치', unpaid_remaining: '남은 미수', same_row_deposit: '같은 행 입금', side_items: '부속 항목', cell: '셀', note: '메모', label: '구분' };
var COL_LABEL = { reg_tax: '등록세', stamp: '증지', fee: '수수료', wonbu_etc: '원부·기타', reissue: '재교부', etc: '기타', subtotal: '소계', total: '합계', deposit: '입금' };
var CODE_LABEL = { copy_kind: { item: '차량 줄', money: '금액 줄' }, amount_from: { M: 'M열 묶음 금액' },
  dup_rule: { D1: '날짜·소계 같음', D2: '소계 같음·날짜 7일 이내', D3: '등록세 같음·날짜 7일 이내', deposit_of_dup_row: '중복 줄의 입금' } };
var FLAG_LABEL = { not_in_excel_total: '엑셀 합계에 안 들어감', group_sum_mismatch: '묶음 합계 불일치', amount_blank: '금액 빈칸', noid_in_excel_total: '차량 정보 없이 합계에 포함',
  parser_kind_carryover: '업무 구분 이어받음', deposit_date_blank: '입금일 빈칸', deposit_date_invalid: '입금일 형식 확인 필요', same_plate_other_row: '같은 번호판 다른 줄 있음',
  copy_section_only: '사본 구역에만 있음', client_col_ambiguous: '거래처 열 확인 필요', plate_in_malso_items: '말소 장부에도 있는 번호판', host_excluded: '같은 행 차량이 제외됨',
  offset_not_cash: '현금 아닌 상계', dup_in_block: '같은 묶음 안 중복', unpaid_candidate: '미수 후보', info_only: '참고용' };
var SRC_LABEL = { office: '구청·지명 열', memo: '비고 열', sheet_name: '시트 이름', col17: '17열' };
var GRADE_LABEL = { H1: '묶음 뒤 입금 합 일치', H2: '묶음 뒤 입금 합 다름', H3: '거래처 여럿', H3b: '거래처 빈칸', H4: '직전 묶음 없음' };

var fmt = function (n) { return (parseInt(n, 10) || 0).toLocaleString(); };
// detail 값 표시 — 입금 묶음({amount,date,payer})은 읽을 수 있게, 그 외 객체는 JSON 그대로
var valText = function (v) {
  if (v == null || v === '') return '';
  if (Array.isArray(v)) return v.map(valText).join(', ');
  if (typeof v === 'object') return v.amount != null ? [fmt(v.amount) + '원', v.date, v.payer].filter(Boolean).join(' · ') : JSON.stringify(v);
  return String(v);
};
// ref_rows = [[시트, 행]] 또는 [[말소 시트, 행, 업무]] → "시트 120행 (업무)"
var refRowsText = function (v) {
  return (Array.isArray(v) ? v : []).map(function (e) {
    return Array.isArray(e) ? e[0] + ' ' + e[1] + '행' + (e[2] ? ' (' + e[2] + ')' : '') : String(e);
  }).join(', ');
};
var detailText = function (k, v) {
  if (k === 'cols') return Object.keys(v || {}).map(function (c) { return (COL_LABEL[c] || c) + ' ' + fmt(v[c]); }).join(' · ');
  if (k === 'ref_rows') return refRowsText(v);
  if (k === 'date_inherited') return v ? '위 줄 날짜를 이어받음' : '';
  if (k === 'group_start' || k === 'host_row') return v ? v + '행' : '';
  if (k === 'group_total' || k === 'unpaid_remaining') return fmt(v) + '원';
  if (k === 'side_items') return [v && v['내역'], Array.isArray(v && v.amounts) ? v.amounts.map(fmt).join(' / ') : ''].filter(Boolean).join(' · ');
  if (CODE_LABEL[k]) return CODE_LABEL[k][v] || valText(v);
  return valText(v);
};
var badge = function (text, bg, color) {
  return (<span style={{ display: 'inline-block', marginLeft: 5, padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: bg, color: color, whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{text}</span>);
};
var td = { padding: '6px', fontSize: 12, whiteSpace: 'nowrap', borderTop: '1px solid #f1f5f9', verticalAlign: 'top' };
// v5 반영 상태 (recordOut postedState: 0 미반영 · 1 반영 · 2 장부에서 고침(분리) · 3 ERP 중복 제외) — 0 이 아니면 연결을 바꿀 수 없다
var POSTED_BADGE = { 1: ['장부 반영됨', '#dcfce7', '#166534'], 2: ['장부에서 고침', '#fef3c7', '#92400e'], 3: ['ERP 중복 제외', '#e2e8f0', '#475569'] };
var postedBadge = function (r) { var b = POSTED_BADGE[r.postedState]; return b ? badge(b[0], b[1], b[2]) : null; };
var unitCell = function (r) {
  if (!(r.unitId > 0)) return <span style={{ color: '#94a3b8' }}>미연결{postedBadge(r)}</span>;
  return (<span>{r.unitName || '-'}{r.linkMode === 'auto' && badge('자동 연결', '#ecfdf5', '#047857')}{postedBadge(r)}</span>);
};

// 탭별 열 — [제목, 그리기, 오른쪽 정렬]
export function recordCols(tab, jobKind) {
  var loc = function (r) { return <span style={{ color: '#64748b' }}>{r.sheet} {r.row}행</span>; };
  var money = function (r) { return fmt(r.amount); };
  if (tab === 'items') {
    return [['날짜', function (r) { return r.date || '-'; }], ['원본 위치', loc], ['차종', function (r) { return r.carType || '-'; }],
      ['차량번호', function (r) { return <b>{r.plate || '-'}{r.recKind === 'side_item' && badge('부속표', '#fef3c7', '#92400e')}</b>; }],
      ['차대번호', function (r) { return r.vin || '-'; }], ['금액', money, 1], ['거래처 원문', function (r) { return r.clientRaw || '-'; }]]
      .concat(jobKind === '이전' ? [['입금', function (r) { return valText(r.detail && r.detail.same_row_deposit) || '-'; }]] : [])
      .concat([['연결 업체', unitCell]]);
  }
  if (tab === 'deposits') {
    return [['날짜', function (r) { return r.date || '-'; }], ['원본 위치', loc], ['입금자', function (r) { return r.payerRaw || '-'; }], ['금액', money, 1],
      ['참고', function (r) { return r.hintClient ? <span style={{ color: '#94a3b8' }}>참고: {r.hintClient}</span> : ''; }], ['연결 업체', unitCell]];
  }
  return [['날짜', function (r) { return r.date || '-'; }], ['원본 위치', loc], ['차량번호', function (r) { return r.plate || '-'; }], ['금액', money, 1],
    ['거래처 원문', function (r) { return r.clientRaw || '-'; }], ['제외 사유', function (r) { return EXCLUDE_LABEL[r.excludeReason] || r.excludeReason || '-'; }],
    ['참조 행', function (r) { return refRowsText(r.detail && r.detail.ref_rows) || '-'; }]];
}

export default function RecordsRow(props) {
  var r = props.row, cols = props.cols, linkable = props.linkable, hist = props.hist, busy = props.busy;
  var isMobile = useIsMobile();
  var colCount = cols.length + (linkable ? 1 : 0);
  var locked = (parseInt(r.postedState, 10) || 0) > 0;

  var detailPanel = function () {
    var d = r.detail || {};
    var fields = [['거래처 원문', (r.clientRaw || '') + (r.clientSrc ? ' (' + (SRC_LABEL[r.clientSrc] || r.clientSrc) + ')' : '')], ['명의자', r.ownerRaw], ['입금자', r.payerRaw],
      ['참고 거래처', r.hintClient ? r.hintClient + (r.hintGrade ? ' · ' + (GRADE_LABEL[r.hintGrade] || r.hintGrade) : '') : ''], ['비고', r.memo],
      ['확인 표시', (r.reviewFlags || []).map(function (f) { return FLAG_LABEL[f] || f; }).join(', ')]]
      .concat(Object.keys(d).map(function (k) { return [DETAIL_LABEL[k] || k, detailText(k, d[k])]; }))
      .filter(function (f) { return !!f[1]; });
    return (<div data-testid="record-detail" style={{ padding: '10px 12px', background: '#f8fafc', whiteSpace: 'normal' }}>
      {linkable && <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8, fontSize: 12.5 }}>
        <span style={{ fontWeight: 700 }}>연결 업체:</span> {unitCell(r)}
        {r.unitId > 0 && !!r.accountName && r.accountName !== r.unitName && <span style={{ color: '#94a3b8' }}>· {r.accountName}</span>}
        {r.unitId > 0 && <span style={{ color: '#94a3b8' }}>{r.linkedByName || ''}{r.linkedAt ? ' · ' + formatArrivedAtKst(r.linkedAt) : ''}</span>}
        {locked && <span style={{ color: '#92400e' }}>장부에 반영된 기록이라 연결을 바꿀 수 없습니다. 바꾸려면 장부 반영 카드에서 반영을 취소해 주세요.</span>}
        {!locked && <button className="btn btn-outline btn-sm" disabled={busy} onClick={props.onPick}>{r.unitId > 0 ? '연결 변경' : '업체 연결'}</button>}
        {!locked && r.unitId > 0 && <button className="btn btn-outline btn-sm" disabled={busy} onClick={props.onUnlink}>연결 해제</button>}
      </div>}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '4px 16px', fontSize: 12 }}>
        {fields.map(function (f, i) { return <div key={i}><span style={{ color: '#94a3b8' }}>{f[0]}</span> <span style={{ color: '#334155' }}>{f[1]}</span></div>; })}
      </div>
      {linkable && <div style={{ marginTop: 8, fontSize: 12 }}>
        <div style={{ fontWeight: 700, color: '#475569', marginBottom: 2 }}>연결 이력</div>
        {hist === null ? <span style={{ color: '#94a3b8' }}>불러오는 중…</span>
          : !hist.length ? <span style={{ color: '#94a3b8' }}>이력 없음</span>
            : hist.map(function (h, i) {
              return <div key={i} style={{ color: '#475569' }}>{formatArrivedAtKst(h.at) || '-'} · {MODE_LABEL[h.mode] || h.mode} · {(h.fromUnitName || '미연결') + ' → ' + (h.toUnitName || '미연결')} · {h.actorName || '-'}</div>;
            })}
      </div>}
    </div>);
  };

  return (<React.Fragment>
    <tr data-testid="record-row" onClick={props.onToggle} style={{ cursor: 'pointer', background: props.open ? '#f0f4f8' : '#fff' }}>
      {linkable && <td style={td} onClick={function (e) { e.stopPropagation(); }}><input type="checkbox" aria-label={'선택 ' + r.id} checked={!!props.selected}
        onChange={props.onSelect} /></td>}
      {cols.map(function (c) { return <td key={c[0]} style={c[2] ? Object.assign({}, td, { textAlign: 'right' }) : td}>{c[1](r)}</td>; })}
    </tr>
    {props.open && <tr><td colSpan={colCount} style={{ padding: 0, borderTop: '1px solid #e5e7eb' }}>{detailPanel()}</td></tr>}
  </React.Fragment>);
}
